import {
  TRACK_PLAYLISTED_FROM_SIM_SEARCH,
  TRACK_PLAYLISTED_FROM_SIM_TAB,
} from '@disco/constants/EventTypes'
import { PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import {
  BRIEF_INBOX,
  BRIEFS,
  CUSTOM_CHANNEL,
  CUSTOM_INBOX,
  PERSONAL_INBOX,
  PERSONAL_LIBRARY,
  TEAM_INBOX,
  TEAM_LIBRARY,
  TERRITORY,
} from '@disco/constants/GroupTypes'
import { getResultsSourceLabel } from '@disco/utils/search-page'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'
import type { V2SearchQuery } from 'types/search-page'
import { getDiscoverDragAndDropTrackProperties } from './discover-page'

const ID_LIMIT = 50

const SEARCH_TYPE_LABELS: Record<V2SearchQuery['type'], string> = {
  regular: 'Keyword',
  simsearch: 'Similarity',
  llm: 'Prompt',
}

export type BrowseSearchDragData = {
  location: string
  context: string
  source: string
  searchType: string
  searchId?: string
  resultsSource: string
}

/**
 * The search details a drag from Browse search results carries, so
 * `Drag and drop item(s)` gets the same properties as on the /search page.
 */
export const getBrowseSearchDragData = (
  searchQuery: V2SearchQuery,
  aimsEnabled: boolean,
): BrowseSearchDragData => ({
  location: 'Explorer',
  context: 'Browse',
  source: 'Search results',
  searchType: SEARCH_TYPE_LABELS[searchQuery.type],
  searchId: searchQuery.event_data?.search_id,
  resultsSource: getResultsSourceLabel(
    aimsEnabled,
    searchQuery.type === 'regular',
  ),
})

const GROUP_TYPE_LABELS: Record<string, string> = {
  [TEAM_LIBRARY]: 'Browse',
  [PERSONAL_LIBRARY]: 'Starred',
  [CUSTOM_CHANNEL]: 'Custom Channel',
  [PERSONAL_INBOX]: 'Personal Inbox',
  [TEAM_INBOX]: 'Team Inbox',
  [CUSTOM_INBOX]: 'Custom Inbox',
  [BRIEF_INBOX]: 'Brief Inbox',
  [TERRITORY]: 'Territory',
  [BRIEFS]: 'Briefs',
}

const getGroupTypeLabel = (groupType?: string | null) =>
  (groupType && GROUP_TYPE_LABELS[groupType]) || groupType || undefined

const getDragItemType = (draggedItem: any, selectedItems: any[]) => {
  if (selectedItems.length > 0) {
    const hasTrack = selectedItems.some(item => item.type === TRACK)
    const hasPlaylist = selectedItems.some(item => item.type === PLAYLIST)
    if (hasTrack && hasPlaylist) return 'Mixed'
    return hasPlaylist ? 'Playlist' : 'Track'
  }
  return draggedItem?.playlist ? 'Playlist' : 'Track'
}

const getTrackIds = (draggedItem: any, selectedItems: any[]) => {
  if (selectedItems.length > 0) {
    return selectedItems
      .filter(item => item.type === TRACK)
      .map(item => item.id)
  }
  if (draggedItem?.track) return [draggedItem.track.id]
  return []
}

const getPlaylistIds = (draggedItem: any, selectedItems: any[]) => {
  if (selectedItems.length > 0) {
    return selectedItems
      .filter(item => item.type === PLAYLIST)
      .map(item => item.playlist_id)
  }
  if (draggedItem?.playlist) return [draggedItem.playlist.playlist_id]
  return []
}

const getFromLabel = (
  draggedItem: any,
  isFromSimilarTracksTab: boolean,
  simSearchUrl: string | undefined,
) => {
  if (isFromSimilarTracksTab) return 'Similar tracks'
  if (simSearchUrl) return 'Search results'
  return draggedItem?.source || getGroupTypeLabel(draggedItem?.sourceGroupType)
}

const filterDraggedItemProperties = (draggedItem: any) => {
  if (draggedItem?.playlistId) {
    const { playlist, playlistBusiness, playlistId } = draggedItem
    return {
      playlist,
      playlistId,
      playlistBusiness,
    }
  } else if (draggedItem?.catalogId) {
    const { catalogName, catalogId, catalogBusiness } = draggedItem
    return {
      catalogName,
      catalogId,
      catalogBusiness,
    }
  }
}

export const getMixpanelDragAndDropProps = (
  draggedItem,
  destination: string,
) => {
  const isAggregatedSearch = isAggregatedSearchTrack(draggedItem.track)
  const selectedItems = draggedItem?.selectedItems || []
  const track = draggedItem?.track

  const trackIds = getTrackIds(draggedItem, selectedItems)
  const playlistIds = getPlaylistIds(draggedItem, selectedItems)

  const itemCatalogs = selectedItems
    .map(
      item =>
        item?.origin?.source_library?.id || item?.origin?.primary_library?.id,
    )
    .filter(Boolean)

  const itemBusinesses = selectedItems
    .map(item => item?.origin?.business?.name)
    .filter(Boolean)

  const itemBusinessIds = selectedItems
    .map(item => item?.origin?.business?.id)
    .filter(Boolean)

  const isDiscoverItem = draggedItem?.id?.includes('discover')
  if (isDiscoverItem) {
    const filteredProperties = filterDraggedItemProperties(draggedItem)
    return getDiscoverDragAndDropTrackProperties(
      {
        trackId: track?.id,
        ...filteredProperties,
        context: draggedItem?.context,
      },
      destination,
    )
  }

  const cappedTrackIds = trackIds.slice(0, ID_LIMIT)
  const cappedPlaylistIds = playlistIds.slice(0, ID_LIMIT)

  const count = selectedItems.length || (track || draggedItem?.playlist ? 1 : 0)

  const simSearchUrl =
    track?.internal_data?.[TRACK_PLAYLISTED_FROM_SIM_SEARCH]?.url
  const isFromSimilarTracksTab = Boolean(
    track?.internal_data?.[TRACK_PLAYLISTED_FROM_SIM_TAB],
  )

  const mixpanelProperties = {
    'Track ID': cappedTrackIds,
    'Playlist ID': cappedPlaylistIds,
    'Source Playlist ID': draggedItem?.sourcePlaylistId,
    Catalog: itemCatalogs?.length
      ? itemCatalogs
      : track?.origin?.source_library?.id || track?.origin?.primary_library?.id,
    'Catalog Business': itemBusinesses?.length
      ? itemBusinesses
      : track?.origin?.business?.name,
    'Catalog Business ID': itemBusinessIds?.length
      ? itemBusinessIds
      : track?.origin?.business?.id,
    Location: draggedItem?.location || 'Explorer',
    From: getFromLabel(draggedItem, isFromSimilarTracksTab, simSearchUrl),
    Context: draggedItem?.context,
    Search:
      draggedItem?.searchType || (simSearchUrl ? 'Similarity' : undefined),
    MDS: isAggregatedSearch,
    Destination: destination,
    Type: getDragItemType(draggedItem, selectedItems),
    Count: count,
    'Search ID': draggedItem?.searchId,
    URL: simSearchUrl,
    Source: draggedItem?.resultsSource,
  }

  return mixpanelProperties
}
